import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserContext } from '../ContextProviders';
import { useNovaChat } from '@/hooks/useNovaChat';
import { useNova } from './NovaContext';
import NovaLandingSidebar from './NovaLandingSidebar';
import { novaThemes, novaDisclaimer } from './novaTheme';
import { NovaChevronRightIcon } from './novaIcons';

const NOVA_HOME_HREF = '/tools/nova-ai';

// Trending questions across the Stylus library — the Nova Discover Questions list.
const TRENDING_QUESTIONS = [
  'Give me key emerging consumer segments',
  'Give me stats on smart home adoption',
  'What ingredients will trend next year?',
  'What are trends in family travel?',
  'What are the latest luxury fashion trends?',
  'What beauty trends are resonating with Gen X?',
  'What are the latest bathroom design trends?',
  'What are the colour trends for S/S 27?',
  'How are brands engaging young consumers?',
  'How is boyhood and girlhood changing?',
];

// The "Discover" full-page state from the Nova buildout handoff (Nova Page
// Discover). Reuses the same rail as NovaLanding/NovaTemplates so all of Nova's
// full-page states read as one product; tapping a question hands it to a fresh
// Nova conversation via NovaLanding's ?q= seed, same handoff as "Build a Report".
const NovaDiscover: React.FC = () => {
  const router = useRouter();
  const user = useContext(UserContext);
  const { theme } = useNova();
  const { conversations, conversationsLoading, loadConversations, deleteConversations } = useNovaChat();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const surfaces = novaThemes[theme];

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAskQuestion = (question: string) => {
    router.push(`${NOVA_HOME_HREF}?q=${encodeURIComponent(question)}`);
  };

  return (
    <div
      className="nova-full-page flex h-[calc(100dvh-var(--header-height))] flex-col gap-0 overflow-hidden md:flex-row"
      style={{ background: surfaces.surfaces.panelBase, color: surfaces.ink.body }}
    >
      <NovaLandingSidebar
        theme={theme}
        active="discover"
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(true)}
        onExpand={() => setSidebarCollapsed(false)}
        onNewChat={() => router.push(NOVA_HOME_HREF)}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        onSelectConversation={(id) => router.push(`${NOVA_HOME_HREF}?c=${encodeURIComponent(id)}`)}
        onDeleteConversation={(id) => deleteConversations([id])}
        userName={user?.name}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1 overflow-y-auto" data-lenis-prevent>
          <div className="mx-auto max-w-[1040px] px-4 pt-6 pb-8 sm:px-8 md:px-12 md:pt-11 md:pb-10">
            <div className="mb-2.5 text-xs tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
              Discover
            </div>
            <h1
              className="mb-3 max-w-[900px] font-sohne text-[28px] leading-[32px] font-semibold tracking-[-0.6px] md:text-[40px] md:leading-[44px] md:tracking-[-1.1px] text-pretty"
              style={{ color: surfaces.ink.heading }}
            >
              Try asking
            </h1>
            <p className="mb-6 max-w-[620px] text-base leading-[24px] text-pretty md:mb-8 md:text-lg md:leading-[27px]" style={{ color: surfaces.ink.body }}>
              Example questions to explore the Stylus library. Tap any to run it.
            </p>

            <div className="mb-3 text-[11px] tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
              Trending
            </div>
            <div className="flex flex-col border-t" style={{ borderColor: surfaces.surfaces.hairline }}>
              {TRENDING_QUESTIONS.map((question, index) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => handleAskQuestion(question)}
                  className="flex cursor-pointer items-center gap-4 border-0 border-b bg-transparent px-1 py-3.5 text-left md:py-[18px]"
                  style={{ borderColor: surfaces.surfaces.hairline }}
                >
                  <span className="w-[22px] flex-none text-[13px] font-semibold" style={{ color: surfaces.ink.secondary }}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1 text-[15px] leading-[21px] md:text-[17px] md:leading-[23px]" style={{ color: surfaces.ink.heading }}>
                    {question}
                  </span>
                  <span className="flex-none" style={{ color: surfaces.ink.secondary }}>
                    <NovaChevronRightIcon className="h-[18px] w-[18px]" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          className="shrink-0 px-4 pt-2 pb-[18px] text-center text-xs md:text-sm"
          style={{ color: surfaces.ink.placeholder }}
        >
          {novaDisclaimer.short}
        </div>
      </div>
    </div>
  );
};

export default NovaDiscover;
